// Keep this file definitions-only. Dispatchers import these helpers while some
// registered modal components import dispatchers, so registrations live in
// modalRegistrations.ts to avoid circular initialization.
import type { RootModalDrawerOptions } from 'components/Drawers/standardDrawerContract';

import type { AsyncModalModule, ModalComponent, RegistryComponent } from './types';

export const MODAL_COMPONENTS: Record<string, RegistryComponent | undefined> = {};
const MODAL_IMPORTERS: Record<string, (() => Promise<unknown>) | undefined> = {};
const MODAL_IMPORT_PROMISES: Record<string, Promise<AsyncModalModule> | undefined> = {};
const MODAL_LOADED: Record<string, ModalComponent | undefined> = {};
const MODAL_LOAD_FAILED: Record<string, boolean | undefined> = {};
const MODAL_DRAWER_OPTIONS: Record<string, RootModalDrawerOptions | undefined> = {};
const MODAL_PRESENTATION: Record<string, string | undefined> = {};

type ModalRegistrationOptions = {
  rootModalDrawer?: RootModalDrawerOptions;
  // A registry default presentation, e.g. 'modal' to keep a type off the drawer
  // plane even when launched from a drawer. Record-level props still win.
  rootModalPresentation?: string;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const toModalComponent = (module: AsyncModalModule): ModalComponent =>
  (isObject(module) && module.default ? module.default : module) as ModalComponent;

const loadModalComponent = (key: string, getComponent: () => Promise<unknown>): Promise<AsyncModalModule> => {
  MODAL_LOAD_FAILED[key] = false;
  return getComponent().then(
    (module) => {
      MODAL_LOADED[key] = toModalComponent(module);
      return module;
    },
    (error: unknown) => {
      MODAL_LOAD_FAILED[key] = true;
      throw error;
    },
  );
};

export const preloadModalComponent = (key: string): Promise<AsyncModalModule> | null => {
  const getComponent = MODAL_IMPORTERS[key];
  if (!getComponent) {
    return null;
  }

  if (process.env.NODE_ENV === 'development') {
    return loadModalComponent(key, getComponent);
  }

  // Forget a failed load so the next open calls import() again; the browser may keep failing it until a reload.
  MODAL_IMPORT_PROMISES[key] ??= loadModalComponent(key, getComponent).catch((error: unknown) => {
    MODAL_IMPORT_PROMISES[key] = undefined;
    throw error;
  });
  return MODAL_IMPORT_PROMISES[key] ?? null;
};

// The component of a lazily registered modal whose chunk has already loaded, so it can render without a
// loading frame.
export const getLoadedModalComponent = (key: string): ModalComponent | undefined => MODAL_LOADED[key];

// A lazily registered modal whose code hasn't loaded yet, unless its last load failed.
export const isModalComponentPending = (key: string): boolean =>
  Boolean(MODAL_IMPORTERS[key]) && !MODAL_LOADED[key] && !MODAL_LOAD_FAILED[key];

export const isModalComponentPreloadable = (key: string): boolean => Boolean(MODAL_IMPORTERS[key]);

// Every registered type. Lazily registered types are non-enumerable getters on MODAL_COMPONENTS, so Object.keys
// misses them; reading the names this way also avoids calling a getter, which would load that modal's code.
export const getRegisteredModalTypes = (): string[] => Object.getOwnPropertyNames(MODAL_COMPONENTS);

export const getRegisteredRootModalDrawerOptions = (key: string): RootModalDrawerOptions | undefined =>
  MODAL_DRAWER_OPTIONS[key];

export const getRegisteredRootModalPresentation = (key: string): string | undefined => MODAL_PRESENTATION[key];

const registerModalOptions = (key: string, options?: ModalRegistrationOptions): void => {
  MODAL_DRAWER_OPTIONS[key] = options?.rootModalDrawer;
  MODAL_PRESENTATION[key] = options?.rootModalPresentation;
};

// The default way to register a root modal: the component's code loads the first time the modal opens (or is
// preloaded), so it stays out of the startup bundle. In development the importer runs on every open.
export const ImportModalComponent = (
  key: string,
  getComponent: () => Promise<unknown>,
  options?: ModalRegistrationOptions,
): void => {
  MODAL_IMPORTERS[key] = getComponent;
  registerModalOptions(key, options);
  Object.defineProperty(MODAL_COMPONENTS, key, {
    configurable: true,
    get: async () => toModalComponent(await preloadModalComponent(key)),
  });
};

// Registers an already-imported component. Only for small dialogs that must open even when chunks can't load
// (error and offline paths); everything else uses ImportModalComponent.
export const RegisterModalComponent = (key: string, component: unknown, options?: ModalRegistrationOptions): void => {
  MODAL_COMPONENTS[key] = component as ModalComponent;
  registerModalOptions(key, options);
};
